import { HamburgerButton } from '@icon-park/react'
import { ReactNode, useEffect, useRef, useState } from 'react'

import { Tokens } from '@/assets/tokens'
import { Button } from '@/components'

import { MenuButton, MenuContainer, MenuDropdown, MenuIcon, MenuItem, MenuText } from './ActionsMenu.styles'

export type ActionMenuItem = {
  key: string
  label: string
  icon: ReactNode
  onClick: () => void
  disabled?: boolean
}

export type ActionsMenuProps = {
  actions: ActionMenuItem[]
  disabled?: boolean
  placement?: 'left' | 'right'
  size?: 'small' | 'medium'
  triggerIcon?: ReactNode
  className?: string
}

export function ActionsMenu({
  actions,
  disabled = false,
  placement = 'right',
  size = 'medium',
  triggerIcon,
  className,
}: ActionsMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleActionClick = (action: ActionMenuItem) => {
    if (!action.disabled) {
      action.onClick()
      setIsOpen(false)
    }
  }

  const toggleMenu = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  return (
    <MenuContainer ref={menuRef} className={className}>
      <MenuButton $variant="link" onClick={toggleMenu} $isOpen={isOpen} $size={size} disabled={disabled}>
        {triggerIcon || (
          <Button.Icon
            value={HamburgerButton}
            color={disabled ? Tokens.ColorTextTertiary : Tokens.ColorTextSecondary}
            size={size === 'small' ? 16 : 20}
          />
        )}
      </MenuButton>

      {isOpen && (
        <MenuDropdown $placement={placement}>
          {actions.map((action) => (
            <MenuItem key={action.key} onClick={() => handleActionClick(action)} disabled={action.disabled}>
              <MenuIcon $disabled={action.disabled}>{action.icon}</MenuIcon>
              <MenuText $disabled={action.disabled}>{action.label}</MenuText>
            </MenuItem>
          ))}
        </MenuDropdown>
      )}
    </MenuContainer>
  )
}
